import styled from '@emotion/styled';
import { Tab, Tabs as _Tabs } from '@material-ui/core';
import { DateTime } from 'luxon';
import { createElement, FC, Fragment, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  DailySummaryPerformanceWithAllFieldsFragment,
  PerformanceReportMetric,
  PeriodicImprovementPerformanceWithAllFieldsFragment,
} from '../../../../generated/graphql';
import { SPACING_REGULAR } from '../../../constant/spacing';
import { flexibleLayoutContainer } from '../../../styleUtility/flexibleLayout';
import { gridLayoutContainer, gridLayoutItem } from '../../../styleUtility/gridLayout';
import Spinner from '../Spinner';
import _DailySummaryPerformanceGraph, { GraphData } from './DailySummaryPerformanceGraph';
import EmptyState from './EmptyState';

const tabNames = ['cost_per_action', 'view_through_cost_per_action', 'click_through_rate', 'video_view_rate'] as const;
const viewRateMetrics = [
  'video_view_rate_p25',
  'video_view_rate_p50',
  'video_view_rate_p75',
  'video_view_rate_p95',
  'video_view_rate_p100',
] as const;

type TabName = typeof tabNames[number];
type ViewRate = typeof viewRateMetrics[number];

type Props = {
  loading: boolean;
  dailySummaryPerformance: DailySummaryPerformanceWithAllFieldsFragment[] | null;
  periodicImprovementPerformance: PeriodicImprovementPerformanceWithAllFieldsFragment | null;
  className?: string;
};

type TabContentProps = {
  loading: boolean;
  data?: GraphData;
  isTabViewRate: boolean;
};

const convertToLineChartData = (performance: DailySummaryPerformanceWithAllFieldsFragment) => {
  const creativeWithValues = performance.creatives.find((creative) => !!creative.values.length);
  if (!creativeWithValues) {
    return undefined;
  }
  const dateArray = creativeWithValues.values.map((value) => {
    return { date: value.date };
  });

  const startDateString = dateArray[0];
  const endDateString = dateArray[dateArray.length - 1];

  let periodDateArray: { date: string }[] = [];
  if (startDateString && endDateString) {
    const startDate = new Date(startDateString.date);
    const endDate = new Date(endDateString.date);

    // データに入っている期間の「一番初めの時期」と「最後の時期」の間にある日付をすべて生成
    for (let date: Date = startDate; date <= endDate; date.setDate(date.getDate() + 1)) {
      periodDateArray = [...periodDateArray, { date: date.toISOString() }];
    }
  }

  const data = periodDateArray.map((item) => {
    const valuesByDate = performance.creatives
      .filter((creative) => !!creative.values.length)
      .reduce((prev, creative) => {
        const targetItem = creative.values.find((value) => {
          return new Date(value.date).getTime() === new Date(item.date).getTime();
        });
        return targetItem
          ? {
              ...prev,
              currency: targetItem.actualValue.currency,
              type: targetItem.actualValue.type,
              [creative.creation.id]: targetItem.actualValue.value,
            }
          : {
              // 該当する日付のデータがなければ0を入れておく
              ...prev,
              currency: creative.values[0].actualValue.currency,
              type: creative.values[0].actualValue.type,
              [creative.creation.id]: 0,
            };
      }, {});

    return {
      date: DateTime.fromISO(item.date).toFormat('MMM dd'),
      ...valuesByDate,
    };
  });

  return data;
};

const convertToViewRateLineChartData = (performance: PeriodicImprovementPerformanceWithAllFieldsFragment) => {
  const viewRateValuesByCreative = performance.creatives.map((creative) => {
    const viewRateValue = creative.values.all.reduce(
      (prev, performanceValue) => {
        if (performanceValue.actualValue) {
          return {
            ...prev,
            [performanceValue.metric]: performanceValue.actualValue.value,
          };
        }

        return prev;
      },
      {} as {
        video_view_rate_p25?: number;
        video_view_rate_p50?: number;
        video_view_rate_p75?: number;
        video_view_rate_p95?: number;
        video_view_rate_p100?: number;
      },
    );

    return {
      creationId: creative.creation!.id,
      ...viewRateValue,
    };
  });

  const data = viewRateMetrics.map((metric: ViewRate) => {
    const values = viewRateValuesByCreative.reduce((prev, creative) => {
      if (creative[metric] !== undefined) {
        return {
          ...prev,
          [creative.creationId]: creative[metric],
        };
      }

      return prev;
    }, {});

    return {
      percent: metric,
      ...values,
    };
  });

  return data;
};

const TabContentContainer: FC<TabContentProps> = ({ loading, data, isTabViewRate }) => {
  if (loading) {
    return (
      <LoadingWrapper>
        <Spinner />
      </LoadingWrapper>
    );
  }

  // XAxisKey 以外のkey を保持しているかどうかチェックする
  const isValidData = data && data.filter((item) => Object.keys(item).length > 1).length > 1;

  return (
    <>
      {data && isValidData ? (
        <DailySummaryPerformanceGraph data={data} isViewRate={isTabViewRate} />
      ) : (
        <EmptyState height={390} />
      )}
    </>
  );
};

const DailySummaryPerformanceSection: FC<Props> = ({
  loading,
  dailySummaryPerformance,
  periodicImprovementPerformance,
  className,
}) => {
  const [activeTab, setActiveTab] = useState<TabName>('cost_per_action');
  const currentDailySummaryPerformance = dailySummaryPerformance
    ? dailySummaryPerformance.filter(
        (dailyPerformance) => dailyPerformance.metric === (activeTab as PerformanceReportMetric),
      )[0]
    : undefined;

  const defaultChartData = useMemo(
    () =>
      dailySummaryPerformance && currentDailySummaryPerformance
        ? convertToLineChartData(currentDailySummaryPerformance)
        : undefined,
    // TODO: Implementation before introducing rules
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dailySummaryPerformance],
  );

  const defaultViewRateData = useMemo(
    () => (periodicImprovementPerformance ? convertToViewRateLineChartData(periodicImprovementPerformance) : undefined),
    [periodicImprovementPerformance],
  );

  const [chartData, setChartData] = useState(defaultChartData);
  const [viewRateData, setViewRateData] = useState(defaultViewRateData);

  const isTabViewRate = activeTab === 'video_view_rate';
  const data = useMemo(() => (isTabViewRate ? viewRateData : chartData), [isTabViewRate, viewRateData, chartData]);

  useEffect(() => {
    setChartData(defaultChartData);
  }, [defaultChartData]);

  useEffect(() => {
    setViewRateData(defaultViewRateData);
  }, [defaultViewRateData]);

  const handleTabChange = (_: any, value: TabName) => {
    setActiveTab(value);
    if (value !== 'video_view_rate') {
      const targetPerformance = dailySummaryPerformance
        ? dailySummaryPerformance.filter(
            (dailyPerformance) => dailyPerformance.metric === (value as PerformanceReportMetric),
          )[0]
        : undefined;
      setChartData(targetPerformance ? convertToLineChartData(targetPerformance) : undefined);
    }
  };

  return (
    <Root className={className}>
      <Tabs value={activeTab} indicatorColor="primary" onChange={handleTabChange}>
        {tabNames.map((tabName) => (
          <Tab key={tabName} value={tabName} label={<FormattedMessage id={`performance_report_metric.${tabName}`} />} />
        ))}
      </Tabs>
      <TabContentContainer loading={loading} data={data} isTabViewRate={isTabViewRate} />
    </Root>
  );
};

const Root = styled('div')({
  ...gridLayoutContainer({
    columns: ['1fr'],
    rows: ['auto', 'auto'],
    areas: [['tabs'], ['graph']],
  }),
});

const Tabs = styled(_Tabs)({
  ...gridLayoutItem('tabs'),
  marginBottom: SPACING_REGULAR,
});

const DailySummaryPerformanceGraph = styled(_DailySummaryPerformanceGraph)({
  ...gridLayoutItem('graph'),
});

const LoadingWrapper = styled('div')({
  height: 390,
  ...flexibleLayoutContainer({
    justifyContent: 'center',
    alignItems: 'center',
  }),
});

export default DailySummaryPerformanceSection;
